/**
 * FileUpload Component
 *
 * Handles file uploads to Firebase Storage
 * Displays uploaded files with remove option
 */

import { useState } from 'react'
import { useForm } from '../../contexts/FormContext'
import { uploadFile, deleteFile, formatFileSize } from '../../services/firebaseStorage'
import './FileUpload.css'

function FileUpload({
  id,
  label,
  accept,
  multiple = false,
  uploadType, // 'logo', 'page', 'additional'
  uploadId = null, // pageId if uploading for specific page
  files = [], // Current uploaded files array
  onChange, // Callback with updated files array
  helperText
}) {
  const { setIsUploading } = useForm()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files)
    if (selectedFiles.length === 0) return

    setUploading(true)
    setIsUploading(true) // Disable navigation
    setError(null)
    const uploadedFiles = []

    for (const file of selectedFiles) {
      try {
        console.log(`Uploading ${file.name}...`)
        const fileData = await uploadFile(file, uploadType, uploadId)
        uploadedFiles.push(fileData)
        console.log(`Upload success:`, fileData)
      } catch (error) {
        console.error('Upload failed:', error)
        setError(`Failed to upload ${file.name}: ${error.message}`)
        break // Stop on first error
      }
    }

    setUploading(false)
    setIsUploading(false) // Re-enable navigation

    if (uploadedFiles.length > 0) {
      // Update parent with new files
      const newFiles = multiple ? [...files, ...uploadedFiles] : uploadedFiles
      onChange(newFiles)
    }

    // Clear input
    e.target.value = ''
  }

  const handleRemove = async (fileToRemove) => {
    if (!confirm(`Remove ${fileToRemove.name}?`)) return

    try {
      console.log('Deleting file:', fileToRemove.storagePath)
      await deleteFile(fileToRemove.storagePath)

      // Update parent with file removed
      const updatedFiles = files.filter(f => f.url !== fileToRemove.url)
      onChange(updatedFiles)
    } catch (error) {
      console.error('Delete failed:', error)
      setError(`Failed to remove ${fileToRemove.name}`)
    }
  }

  return (
    <div className="file-upload-component">
      {label && <label className="file-upload-label">{label}</label>}

      {/* Upload Area */}
      <div
        className={`file-upload-area ${uploading ? 'uploading' : ''}`}
        onClick={() => !uploading && document.getElementById(id).click()}
      >
        <div className="upload-icon">{uploading ? '⏳' : '📎'}</div>
        <div className="upload-text">
          {uploading ? 'Uploading...' : 'Click to upload files'}
        </div>
        {helperText && <div className="upload-hint">{helperText}</div>}
        {accept && <div className="upload-hint-small">Accepted: {accept}</div>}
      </div>

      <input
        type="file"
        id={id}
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        className="file-input-hidden"
        disabled={uploading}
      />

      {/* Error Message */}
      {error && (
        <div className="upload-error">
          ⚠️ {error}
        </div>
      )}

      {/* Uploaded Files List */}
      {files.length > 0 && (
        <div className="uploaded-files-list">
          {files.map((file, index) => (
            <div key={file.url || index} className="uploaded-file-item">
              <div className="file-icon">📄</div>
              <div className="file-info">
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-name"
                  title="Click to view file"
                >
                  {file.name}
                </a>
                <div className="file-size">{formatFileSize(file.size)}</div>
              </div>
              <button
                className="file-remove-btn"
                onClick={() => handleRemove(file)}
                type="button"
                disabled={uploading}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default FileUpload
